/**
 * bimLuminaireMount — the five MOUNTING SPOTS a luminaire can take, and the one
 * rule that turns a spot into a point in the model (card t_0b96786f).
 *
 * The owner's ask, verbatim: *"for lights: to define the mounting spot
 * (seiling, hanging, wall top, wall bottom, standing) thisw would define the
 * ifc model position.. we now have floors and we know ceiling height"*.
 *
 * That sentence is a fact about `bimLuminaireParts.ts`: every variant's ORIGIN
 * is already its mount — a spot's origin is the ceiling cut-out centre, a
 * pendant's the ceiling attachment it hangs from, a wall sconce's the backplate
 * centre ON the wall plane, a pole's the TOP of its head (the unit reaching down
 * to grade). So a mount does not push a fixture along an axis; it IS the point
 * the fixture is built at, and the only two decisions left are which point in
 * the room and which normal.
 *
 * The vocabulary is frozen (plan `2026-10-17-bim-level0-mounts-autoplace.md`,
 * D4): **ceiling · pendant · wallTop · wallBottom · floor** — camelCase, stored
 * on the luminaire's `prefs.mount`. `mountDefaultVariant()` names the fixture a
 * mount builds (D5: `ceiling → spot`, `pendant → pendant`, `wallTop/wallBottom →
 * wall`, `floor → pole`), and `LUMINAIRE_VARIANT_MOUNTS` is the ONE home of the
 * variant↔mount pairing — the editor's Mount and Fixture selects both read it.
 *
 * Rules straight from the plan's table (§3):
 *
 * | mount       | origin Y    | plan position                     | normal                |
 * | ----------- | ----------- | --------------------------------- | --------------------- |
 * | `ceiling`   | `y1`        | room centre                       | `[0, 1, 0]`           |
 * | `pendant`   | `y1`        | room centre, drop clamped         | `[0, 1, 0]`           |
 * | `wallTop`   | `y1 − 0.30` | the longest wall's mid-span       | horizontal, inward    |
 * | `wallBottom`| `y0 + 0.30` | the same wall                     | horizontal, inward    |
 * | `floor`     | `y0 + h`    | room centre                       | `[0, 1, 0]`           |
 *
 * The room's heights are ABSOLUTE model metres (the rebased datum: finished
 * floor 0.00, ground-storey ceiling 2.66, first floor 3.06) — nothing here
 * shifts them, so the same room measures the same in both storeys.
 *
 * The normal is what the scene actually reads (`mountingPlane()` →
 * `placementPlane()` in `utils/bimDeviceModel.ts`): a luminaire renders in the
 * ABSOLUTE frame, and `placement.yawDeg` turns it by the app's own convention
 * (`placementAxes`: `out = R_y(yaw)·(0,0,1)`), which is why the wall's yaw is
 * spelled `atan2(n.x, n.z)` — the same spelling `mountingPlane()` uses. No yaw
 * is invented that the scene would ignore.
 *
 * Pure: no Vue, no three.js, no store, no fetch — `bimLuminaireMount.test.ts`
 * drives every rule below against the two storeys and all four room yaws.
 */

import { LUMINAIRE_DEFAULT_SIZE_MM, type LuminaireVariant } from './bimLuminaireSpec';

/** The five mounting spots, exactly the plan's vocabulary (D4). */
export type LuminaireMount = 'ceiling' | 'pendant' | 'wallTop' | 'wallBottom' | 'floor';

/** In the order the editor offers them: ceiling, hanging, wall top, wall bottom, standing. */
export const LUMINAIRE_MOUNTS: readonly LuminaireMount[] = ['ceiling', 'pendant', 'wallTop', 'wallBottom', 'floor'];

/** True when a stored value names one of the five mounts (an unknown one is no mount). */
export function isLuminaireMount(value: unknown): value is LuminaireMount {
  return typeof value === 'string' && (LUMINAIRE_MOUNTS as readonly string[]).includes(value);
}

/**
 * The variants each mount can seat — the ONE home of the pairing (D5).
 *
 * `ceiling` seats the three flat-to-the-ceiling fixtures (a spot, an
 * under-cabinet panel, a linear strip); `pendant` the two that hang; both wall
 * mounts the sconce; `floor` the pole. Every variant below the `LuminaireVariant`
 * alias must appear exactly once, which is what the coverage test pins.
 */
export const LUMINAIRE_VARIANT_MOUNTS: Record<LuminaireVariant, readonly LuminaireMount[]> = {
  spot: ['ceiling'],
  panel: ['ceiling'],
  strip: ['ceiling'],
  pendant: ['pendant'],
  bulb: ['pendant'],
  wall: ['wallTop', 'wallBottom'],
  pole: ['floor'],
};

/** The mounts each variant can take — derived from the table above, never re-typed. */
export const LUMINAIRE_MOUNT_VARIANTS: Record<LuminaireMount, readonly LuminaireVariant[]> = (() => {
  const out: Record<LuminaireMount, LuminaireVariant[]> = {
    ceiling: [],
    pendant: [],
    wallTop: [],
    wallBottom: [],
    floor: [],
  };
  for (const variant of Object.keys(LUMINAIRE_VARIANT_MOUNTS) as LuminaireVariant[]) {
    for (const mount of LUMINAIRE_VARIANT_MOUNTS[variant]) out[mount].push(variant);
  }
  return out;
})();

/**
 * The fixture a mount builds — the plan's `mount → variant` map (D5):
 * `spot/panel/strip → ceiling`, `pendant/bulb → pendant`, `wall → wallTop`,
 * `pole → floor`. The first variant of the mount's list is its default, so the
 * table above stays the only place a pairing is written.
 */
export function mountDefaultVariant(mount: LuminaireMount): LuminaireVariant {
  return LUMINAIRE_MOUNT_VARIANTS[mount][0] ?? 'spot';
}

/** The mount a variant is seated on by default (`wall → wallTop`, `bulb → pendant`, …). */
export function mountForVariant(variant: LuminaireVariant): LuminaireMount {
  return LUMINAIRE_VARIANT_MOUNTS[variant][0] ?? 'ceiling';
}

/** True when a variant may be seated on a mount (the editor's own compatibility rule). */
export function variantFitsMount(variant: LuminaireVariant, mount: LuminaireMount): boolean {
  return LUMINAIRE_VARIANT_MOUNTS[variant].includes(mount);
}

/**
 * The words the operator reads, in both languages — the owner's own vocabulary
 * (`seiling · hanging · wall top · wall bottom · standing`), beside the module's
 * variant titles in `bimLuminaireParts.ts`. A mount is DATA (the stored word is
 * camelCase, D4); only this table is copy.
 */
export const LUMINAIRE_MOUNT_TITLES: Record<LuminaireMount, { en: string; sl: string }> = {
  ceiling: { en: 'Ceiling', sl: 'Strop' },
  pendant: { en: 'Hanging', sl: 'Viseča' },
  wallTop: { en: 'Wall top', sl: 'Stena zgoraj' },
  wallBottom: { en: 'Wall bottom', sl: 'Stena spodaj' },
  floor: { en: 'Standing', sl: 'Stoječa' },
};

/**
 * A room's own box, as the rooms document carries it (`BimRoom.sections[i]`).
 * `center`/`size` are FLOOR-PLANE pairs `[x, z]`: `size[0]` is the X extent,
 * `size[1]` the Z extent. `y0`/`y1` are absolute model metres.
 */
export interface MountRoom {
  center: [number, number];
  size: [number, number];
  yawDeg: number;
  y0: number;
  y1: number;
}

/** Where a mount puts the fixture: the origin, the mounting normal and the facing. */
export interface MountPlacement {
  x: number;
  y: number;
  z: number;
  /** Unit mounting normal, scene/IFC metres. */
  normal: [number, number, number];
  /** Facing about the world up axis, degrees — the app's own `placementAxes` convention. */
  yawDeg: number;
  /**
   * Pendant only: the drop actually used, metres, clamped so the shade's bottom
   * keeps an 0.8 m clearance above the floor. A caller that writes the slot back
   * writes this (×1000) as the pendant's `prefs.sizeMm.drop`, or the fixture
   * would be drawn through the slab.
   */
  dropM?: number;
}

/** The clearance a hanging fixture keeps above its room's floor (plan §3). */
export const PENDANT_FLOOR_CLEARANCE_M = 0.8;

/** How far below the ceiling a `wallTop` fixture's origin sits (plan §3). */
export const WALL_MOUNT_OFFSET_M = 0.3;

/** The default head height of a standing fixture when the caller knows no size. */
export const DEFAULT_FIXTURE_HEIGHT_M = 1.6;

/** A stored drop in mm → metres (the size table's unit), or `null` when unusable. */
function dropMetres(prefs: { dropFullMm?: number } | undefined): number | null {
  const raw = prefs?.dropFullMm;
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return null;
  return raw / 1000;
}

/** An angle folded into (−180, 180] — the app's own `normalizeTurnDeg` rule. */
function normalizeYawDeg(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  let value = ((deg % 360) + 360) % 360;
  if (value > 180) value -= 360;
  return value;
}

/** A finite number or the fallback (a room row off the wire is not trusted). */
function finite(value: number, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * The one placement rule: a mount and a room box → the fixture's origin, its
 * mounting normal and its facing.
 *
 * `fixtureHeightM` is the STANDING fixture's head height — the pole's origin is
 * the top of its head, so a caller that knows the fixture's own height passes
 * it (the editor passes `prefs.sizeMm.h / 1000`); without it the default 1.6 m
 * stands. For every other mount the argument is ignored.
 */
export function mountPlacement(input: {
  room: MountRoom;
  mount: LuminaireMount;
  prefs?: { dropFullMm?: number };
  fixtureHeightM?: number;
}): MountPlacement {
  const { room, mount } = input;
  const centerX = finite(room.center[0], 0);
  const centerZ = finite(room.center[1], 0);
  const sizeX = Math.max(0, finite(room.size[0], 0));
  const sizeZ = Math.max(0, finite(room.size[1], 0));
  const yawDeg = finite(room.yawDeg, 0);
  const y0 = finite(room.y0, 0);
  const y1 = finite(room.y1, y0 + 1);
  const vertical: [number, number, number] = [0, 1, 0];

  switch (mount) {
    case 'ceiling':
      return { x: centerX, y: y1, z: centerZ, normal: vertical, yawDeg: normalizeYawDeg(yawDeg) };

    case 'pendant': {
      // The pendant's origin is the ceiling attachment; the DROP is what the
      // room limits. The module's own default drop is the fallback, so a caller
      // with no stored size and a low room still gets a legal fixture.
      const requested = dropMetres(input.prefs) ?? LUMINAIRE_DEFAULT_SIZE_MM.pendant.drop / 1000;
      const roomHeight = Math.max(0, y1 - y0);
      const dropM = Math.min(requested, Math.max(0, roomHeight - PENDANT_FLOOR_CLEARANCE_M));
      return { x: centerX, y: y1, z: centerZ, normal: vertical, yawDeg: normalizeYawDeg(yawDeg), dropM };
    }

    case 'wallTop':
    case 'wallBottom': {
      // The LONGEST wall, and its inward normal. A ±X face spans the Z extent
      // (`size[1]`), a ±Z face spans the X extent (`size[0]`); on a tie the plan
      // prefers +X. The point is the face's mid-span, and both the point and the
      // normal turn with the room's own yaw about its centre (the scene's
      // `placementAxes` rotation about +Y).
      const longestIsXFace = sizeZ >= sizeX;
      const localX = longestIsXFace ? sizeX / 2 : 0;
      const localZ = longestIsXFace ? 0 : sizeZ / 2;
      const localNormalX = longestIsXFace ? -1 : 0;
      const localNormalZ = longestIsXFace ? 0 : -1;
      const rad = (yawDeg * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      // R_y(yaw): (x', z') = (cos·x + sin·z, −sin·x + cos·z) — the same
      // `yawed()` shape `bimDeviceModel.placementAxes` uses.
      const worldX = centerX + cos * localX + sin * localZ;
      const worldZ = centerZ - sin * localX + cos * localZ;
      const normalX = cos * localNormalX + sin * localNormalZ;
      const normalZ = -sin * localNormalX + cos * localNormalZ;
      const y = mount === 'wallTop' ? y1 - WALL_MOUNT_OFFSET_M : y0 + WALL_MOUNT_OFFSET_M;
      return {
        x: worldX,
        y,
        z: worldZ,
        normal: [normalX, 0, normalZ],
        // The sconce looks along the wall's inward normal — the one number the
        // scene turns the fixture's body toward the room with.
        yawDeg: normalizeYawDeg((Math.atan2(normalX, normalZ) * 180) / Math.PI),
      };
    }

    case 'floor': {
      const height = finite(input.fixtureHeightM ?? DEFAULT_FIXTURE_HEIGHT_M, DEFAULT_FIXTURE_HEIGHT_M);
      return {
        x: centerX,
        y: y0 + Math.max(0, height),
        z: centerZ,
        normal: vertical,
        yawDeg: normalizeYawDeg(yawDeg),
      };
    }
  }
}
